import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, X, Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { usePatientLocation } from '@/hooks/usePatientLocation';

interface LocationPromptModalProps {
  onLocationSelected?: () => void;
}

export const LocationPromptModal: React.FC<LocationPromptModalProps> = ({ onLocationSelected }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    hasLocation,
    isLocating,
    requestGpsLocation,
    setCep,
  } = usePatientLocation();

  useEffect(() => {
    // Checa se o usuário já tomou uma decisão prévia
    const dismissed = localStorage.getItem('medikei_location_prompt_dismissed');
    if (!hasLocation && !dismissed) {
      // Abre o modal suavemente 600ms após o carregamento inicial da página
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 600);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [hasLocation]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('medikei_location_prompt_dismissed', 'true');
  };

  const handleGpsClick = async () => {
    setErrorMessage(null);
    const coords = await requestGpsLocation();
    if (coords) {
      setIsOpen(false);
      localStorage.setItem('medikei_location_prompt_dismissed', 'true');
      if (onLocationSelected) onLocationSelected();
    } else {
      setErrorMessage('Não foi possível obter sua localização via GPS. Digite seu CEP ou cidade.');
    }
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const clean = manualInput.replace(/\D/g, '');
    if (clean.length === 8) {
      setIsLoadingCep(true);
      setErrorMessage(null);
      try {
        await setCep(clean);
        setIsOpen(false);
        localStorage.setItem('medikei_location_prompt_dismissed', 'true');
        if (onLocationSelected) onLocationSelected();
      } catch {
        setErrorMessage('CEP não encontrado. Verifique os números digitados.');
      } finally {
        setIsLoadingCep(false);
      }
    } else if (manualInput.trim().length >= 3) {
      // Salva cidade digitada
      localStorage.setItem('medikei_patient_address_label', manualInput.trim());
      setIsOpen(false);
      localStorage.setItem('medikei_location_prompt_dismissed', 'true');
      if (onLocationSelected) onLocationSelected();
    } else {
      setErrorMessage('Digite um CEP válido com 8 dígitos ou sua cidade.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-md bg-card text-card-foreground rounded-3xl border border-border shadow-2xl p-6 sm:p-7 overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Botão Fechar */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 p-2 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
          aria-label="Fechar"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Ícone e Cabeçalho */}
        <div className="flex flex-col items-center text-center space-y-3 pt-2">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shadow-inner">
            <MapPin className="w-7 h-7" />
          </div>
          <div>
            <h3 className="text-xl font-black text-foreground tracking-tight">
              Onde você está localizado?
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 leading-relaxed">
              Ative sua localização para encontrar médicos com atendimento presencial em{' '}
              <strong className="text-foreground">consultório</strong> ou{' '}
              <strong className="text-foreground">visita domiciliar</strong> próximos a você.
            </p>
            <p className="text-[11px] text-primary/90 mt-1 font-semibold">
              * Consultas por Telemedicina continuam disponíveis para todo o Brasil.
            </p>
          </div>
        </div>

        {/* Botão de Localização Automática (GPS) */}
        <div className="mt-6 space-y-3">
          <Button
            type="button"
            onClick={handleGpsClick}
            disabled={isLocating}
            className="w-full h-12 rounded-xl font-bold text-sm bg-primary text-white shadow-md shadow-primary/25 hover:bg-primary-hover flex items-center justify-center gap-2 cursor-pointer transition-all"
          >
            {isLocating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Obtendo localização...</span>
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4" />
                <span>Usar minha localização atual (GPS)</span>
              </>
            )}
          </Button>

          <div className="relative my-3">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase font-bold tracking-wider">
              <span className="bg-card px-2.5 text-muted-foreground">ou informe seu CEP / Cidade</span>
            </div>
          </div>

          {/* Formulário Manual */}
          <form onSubmit={handleManualSubmit} className="space-y-2.5">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder="Ex: 01310-100 ou São Paulo, SP"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background text-sm font-semibold text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            {errorMessage && (
              <p className="text-xs text-rose-500 font-medium px-1">{errorMessage}</p>
            )}

            <Button
              type="submit"
              variant="outline"
              disabled={isLoadingCep || manualInput.trim().length < 3}
              className="w-full h-11 rounded-xl font-bold text-xs border-border hover:border-primary hover:text-primary transition-colors cursor-pointer"
            >
              {isLoadingCep ? 'Buscando CEP...' : 'Salvar Localidade'}
            </Button>
          </form>
        </div>

        {/* Rodapé Opcional */}
        <div className="mt-4 pt-3 border-t border-border/60 text-center">
          <button
            type="button"
            onClick={handleClose}
            className="text-xs text-muted-foreground hover:text-foreground font-semibold transition-colors cursor-pointer"
          >
            Agora não, ver todos os médicos
          </button>
        </div>
      </div>
    </div>
  );
};
